import React, { useCallback, useLayoutEffect, useState, useSyncExternalStore } from 'react';

import { ActionBarActions } from './ActionBarActions';
import { actionInvocationOrigin } from './actionInvocationOrigin';
import {
  ActionMirrorSource, actionMirrorScope, invokeMirroredAction, mirrorSnapshot, subscribeActionMirror,
} from './actionMirrorStore';
import { PendingDesignAction } from './PendingDesignAction';
import './StickyPlanningActions.css';

interface Props { projectId?: string | null; sessionId?: string | null; isReadOnly?: boolean; hidden?: boolean }
const attentionMirrorOwners = new WeakMap<HTMLElement, number>();
const sourceKeys = new WeakMap<HTMLElement, number>();
let nextSourceKey = 0;
const sourceKey = (element: HTMLElement): number => {
  if (!sourceKeys.has(element)) sourceKeys.set(element, ++nextSourceKey);

  return sourceKeys.get(element)!;
};
const claimSourceAttention = (element: HTMLElement): (() => void) => {
  attentionMirrorOwners.set(element, (attentionMirrorOwners.get(element) ?? 0) + 1);
  element.dataset.actionAttentionMirrored = 'true';

  return () => {
    const remaining = (attentionMirrorOwners.get(element) ?? 1) - 1;

    if (remaining > 0) attentionMirrorOwners.set(element, remaining);
    else {
      attentionMirrorOwners.delete(element);
      delete element.dataset.actionAttentionMirrored;
    }
  };
};
const MirrorButtons: React.FC<{ source: ActionMirrorSource; scope: string }> = ({ source, scope }) => (
  <ActionBarActions attentionActive={source.attentionActive}
    dataActionChoiceMade={source.actions.some((action) => action.selected)}>
    {source.actions.map((action, index) => <button key={`${source.ownerId}:${index}`} type="button"
      className={`ai-overview-action-btn ${action.stateClasses}`} disabled={action.disabled}
      aria-busy={action.busy || undefined} aria-pressed={action.selected || undefined}
      data-attention-primary={action.attentionPrimary || undefined}
      data-action-placement={action.placement}
      title={action.title || action.label}
      onClick={(event) => invokeMirroredAction(scope, source, action, actionInvocationOrigin(event.currentTarget))}>
      {action.busy ? <span className="ai-overview-action-spinner" aria-hidden="true" />
        : action.iconClass && <i className={action.iconClass} aria-hidden="true" />}
      <span className="ai-overview-action-btn__label">{action.label}</span>
    </button>)}
  </ActionBarActions>
);

const useInlineActionsVisible = (element?: HTMLElement): boolean => {
  const [intersection, setIntersection] = useState<{ element: HTMLElement; visible: boolean } | null>(null);

  useLayoutEffect(() => {
    if (!element || typeof window.IntersectionObserver === 'undefined') return undefined;
    let active = true;
    const observer = new window.IntersectionObserver((entries) => {
      const entry = entries.find((candidate) => candidate.target === element);

      if (active && entry) setIntersection((current) => (
        current?.element === element && current.visible === entry.isIntersecting
          ? current : { element, visible: entry.isIntersecting }
      ));
    });

    observer.observe(element);

    return () => { active = false; observer.disconnect(); };
  }, [element]);

  // Wait for actual viewport geometry before adding a second copy. The default
  // observer root also respects every clipping ancestor of the inline controls.
  return intersection?.element !== element || intersection?.visible !== false;
};

const subscribeViewport = (listener: () => void): (() => void) => {
  window.addEventListener('resize', listener);

  return () => window.removeEventListener('resize', listener);
};
const isMobileViewport = (): boolean => window.innerWidth <= 768;

const useStickyActionSource = ({ projectId, sessionId, isReadOnly, hidden }: Props) => {
  const scope = actionMirrorScope(projectId, sessionId);
  const subscribe = useCallback((listener: () => void) => subscribeActionMirror(scope, listener), [scope]);
  const snapshot = useCallback(() => mirrorSnapshot(scope), [scope]);
  const source = useSyncExternalStore(subscribe, snapshot, () => null);
  const inlineVisible = useInlineActionsVisible(source?.element);
  const mobile = useSyncExternalStore(subscribeViewport, isMobileViewport, () => false);

  // Mobile shares one composer between chat and artifacts. Keep its actions
  // anchored there when the inline source slides back into the chat viewport.
  return { scope, source,
    visible: Boolean(projectId && !isReadOnly && !hidden && source && (mobile || !inlineVisible)) };
};

export const StickyPlanningActions: React.FC<Props> = (props) => {
  const { scope, source, visible } = useStickyActionSource(props);

  useLayoutEffect(() => {
    const sourceElement = visible ? source?.element : null;

    return sourceElement ? claimSourceAttention(sourceElement) : undefined;
  }, [source?.element, visible]);

  if (!source && props.projectId && !props.isReadOnly && !props.hidden) {
    return <PendingDesignAction projectId={props.projectId} sessionId={props.sessionId} />;
  }
  if (!visible || !source) return null;

  return <section className="sticky-planning-actions ai-overview-action-bar notranslate"
    data-sticky-action-mirror="true" translate="no"
    data-source-entry-id={source.ownerId || undefined} role="group" aria-label={`Current actions · ${source.ownerLabel}`}>
    <MirrorButtons key={sourceKey(source.element)} source={source} scope={scope} />
  </section>;
};
